import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { APPLICATION_SUITE_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { ApiService, PreferenceData, UpdatePreferenceResponse } from '@ddt-web/data-service';
import { PartnerPlatformControlsList, WebUserDataConstants } from '@ddt-web/domain';
import { ApplicationStateStoreProxyService, PartnerAcknowledgement } from '@ddt-web/shared-stores';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, first, take, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class PartnerAcknowledgementService {

	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) public readonly partnerPlatformControlsList: PartnerPlatformControlsList;
	private readonly partnerAcknowledgementKey = 'PARTNER_ACKNOWLEDGEMENTS';

	constructor(
		private contentService: ContentService,
		private apiService: ApiService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		@Inject(APPLICATION_SUITE_TOKEN) private applicationSuite: string
	) { }

	public retrievePartnerAcknowledgementInfo(): Observable<PartnerAcknowledgement> {
		return this.applicationStateStoreProxyService.getPartnerAcknowledgementState()
			.pipe(
				tap(partnerPlatformAcknowledgementList => {
					if (isEmpty(partnerPlatformAcknowledgementList)) {
						this.initialisePartnerAcknowledgementList();
					}
				}),
				first(partnerAcknowledgementInfo => !isEmpty(partnerAcknowledgementInfo))
			);
	}

	private initialisePartnerAcknowledgementList(): void {
		combineLatest([
			this.initialiseLatestPartnerAcknowledgementMsgHash(),
			this.initialiseUserPartnerPlatformAcknowledgementList()
		])
			.pipe(take(1))
			.subscribe(([currentAcknowledgementMsgHashList, userAcknowledgementList]) => {
				const children = userAcknowledgementList?.children ?? [];
				const userAcknowledgementMap = children.reduce((acc, item) => {
					const { partnerId, acknowledgementHashKey } = item;
					if (!acc.has(partnerId)) {
						acc.set(partnerId, acknowledgementHashKey);
					}
					return acc;
				}, new Map());

				const partnerAcknowledgementState = {};

				Object.keys(currentAcknowledgementMsgHashList).forEach(partnerId => {
					const currentAcknowledgementMsgHash = currentAcknowledgementMsgHashList[partnerId];
					const lastUserAcknowledgedMsgHash = userAcknowledgementMap.get(partnerId) || '';
					const isUserAcknowledgementRequired = currentAcknowledgementMsgHash !== lastUserAcknowledgedMsgHash;

					partnerAcknowledgementState[partnerId] = {
						currentAcknowledgementMsgHash,
						lastUserAcknowledgedMsgHash,
						isUserAcknowledgementRequired
					};
				});

				this.applicationStateStoreProxyService.updatePartnerAcknowledgementState(partnerAcknowledgementState);
			});
	}

	private initialiseUserPartnerPlatformAcknowledgementList(): Observable<PreferenceData> {
		return this.apiService.fetchPreference(this.partnerAcknowledgementKey)
			.pipe(
				take(1),
				catchError(() => of(null))
			);
	}

	private initialiseLatestPartnerAcknowledgementMsgHash(): Observable<{ [id: string]: string }> {
		return from(this.generateAckDcrHashMap())
			.pipe(take(1));
	}

	private async generateAckDcrHashMap(): Promise<{ [id: string]: string }> {
		const result = {};

		for (const [partnerId, partner] of Object.entries(this.partnerPlatformControlsList)) {
			if (partner.isAcknowledgementRequired) {
				const fetchedString = this.contentService.get(`PARTNER_ACKNOWLEDGEMENT_DISCLAIMER_MSG_${partnerId}`, '');
				const hash = await this.getHashKey(fetchedString);
				result[partnerId] = hash;
			}
		}

		return result;
	};

	private async getHashKey(message: string): Promise<string> {
		const msgBuffer = new TextEncoder().encode(message); // Encode as (utf-8) Uint8Array
		const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer); // Hash the message
		const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert buffer to byte array
		const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // Convert bytes to hex string
		return hashHex;
	}

	public storeUserAcknowledgement(currentAcknowledgmentDcr: string, partnerId: string): Observable<UpdatePreferenceResponse> {
		const preferencesToAdd = {
			acknowledgementHashKey: currentAcknowledgmentDcr,
			partnerId
		};

		const preferenceData: PreferenceData = {
			preferenceKey: this.partnerAcknowledgementKey,
			type: 'PREFERENCE',
			applications: [this.applicationSuite.toUpperCase()],
			children: [{
				...preferencesToAdd,
				type: 'PREFERENCE_CHILD',
				perms: WebUserDataConstants.USER_DATA_PERMS
			}]
		};

		return this.apiService.addPreference(preferenceData).pipe(take(1));
	}
}
